import {
    Badge,
    Button,
    Center,
    Container,
    Grid,
    Group,
    Skeleton,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { Dashlet } from '../components/Dashlet';
import { useDashboard } from '../hooks/useDashboard.tsx';
import { useNavigate } from 'react-router-dom';
import { AiOutlineAppstore } from 'react-icons/ai';
import { useUserAccount } from '../../../hooks/useUserAccount';
import { useUserAttributes } from '../../../hooks/useUserAttributes';
import { BillingSetupCard } from '../../billing-management/customers/components/BillingSetupCard';

const DashletSkeleton = () => (
    <Skeleton height={88} radius="md" />
);

const EmptyState = ({ onBrowse }: { onBrowse: () => void }) => (
    <Center mih={300}>
        <Stack align="center" gap="md">
            <AiOutlineAppstore
                size={64}
                color="var(--mantine-color-dimmed)"
            />
            <Title order={3} ta="center">
                No applications connected yet
            </Title>
            <Text c="dimmed" ta="center" maw={360}>
                Browse available applications to connect them to your account
                and access them from your dashboard.
            </Text>
            <Button
                leftSection={<AiOutlineAppstore />}
                onClick={onBrowse}
            >
                Browse Applications
            </Button>
        </Stack>
    </Center>
);

export const IndexPage = () => {
    const navigate = useNavigate();
    const { applications, loading } = useDashboard();
    const { userAccount } = useUserAccount();
    const { givenName } = useUserAttributes();

    const greeting = givenName ? `Welcome back, ${givenName}` : 'Welcome back';

    return (
        <Container size="lg" id="dashboard-container">
            <Stack gap="xl" py="md">
                {userAccount && !userAccount.customerId && (
                    <BillingSetupCard variant="banner" />
                )}

                <Group justify="space-between" align="flex-end">
                    <Stack gap={4}>
                        <Title order={2}>{greeting}</Title>
                        {!loading && applications.length > 0 && (
                            <Group gap="xs">
                                <Text size="sm" c="dimmed">
                                    Your connected applications
                                </Text>
                                <Badge
                                    variant="light"
                                    size="sm"
                                >
                                    {applications.length}
                                </Badge>
                            </Group>
                        )}
                    </Stack>
                    <Button
                        variant="subtle"
                        leftSection={<AiOutlineAppstore />}
                        onClick={() => navigate('/applications')}
                    >
                        See All Applications
                    </Button>
                </Group>

                {loading ? (
                    <section aria-label="Loading applications">
                        <Grid>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <Grid.Col
                                    key={i}
                                    span={{ xl: 4, lg: 4, md: 6, sm: 12, xs: 12 }}
                                >
                                    <DashletSkeleton />
                                </Grid.Col>
                            ))}
                        </Grid>
                    </section>
                ) : applications.length === 0 ? (
                    <EmptyState onBrowse={() => navigate('/applications')} />
                ) : (
                    <section aria-label="Your applications">
                        <Grid>
                            {applications.map((item, index) => (
                                <Grid.Col
                                    key={index}
                                    span={{ xl: 4, lg: 4, md: 6, sm: 12, xs: 12 }}
                                >
                                    <Dashlet item={item} />
                                </Grid.Col>
                            ))}
                        </Grid>
                    </section>
                )}
            </Stack>
        </Container>
    );
};
